/**
 * Authentication Service - Centralized Error Handling
 * Handles all authentication-related API calls with unified error processing
 * Following Feature-Based Modular Architecture with centralized error service
 */

// Frontend auth service types - internal to frontend
import type { LoginResponse, RegisterResponse, UserResponse } from '@bat/shared'

import { apiService, api, throwBackendError } from '@/platform/api/api.service'
import {
  accountRefusalCodeOf,
  clearSignedOutReason,
  rememberSignedOutReason,
} from '@/platform/auth/services/account-refusal'
import { isMaintenanceRefusal } from '@/platform/maintenance/maintenance-events'
import { errorService } from '@/shared/services/error.service'
import { storageService } from '@/shared/services/storage.service'
import { ApiException } from '@/shared/utils/api'
import { AUTH_CONFIG, STORAGE_KEYS } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

// MFA Types
interface MfaSetupData {
  secret: string
  qrCodeUrl: string
  backupCodes: string[]
}

interface MfaVerifyResponse {
  verified: boolean
  mfaEnabled: boolean
}

interface MfaStatusResponse {
  enabled: boolean
  verifiedAt: string | null
}

interface MfaDisableRequest {
  code?: string
  password?: string
}

interface LoginRequest {
  email: string
  password: string
  rememberMe?: boolean
}

interface RegisterRequest {
  email: string
  password: string
  confirmPassword: string
  agreeToTerms: boolean
}

interface PasswordResetRequest {
  email: string
}

interface PasswordResetConfirmRequest {
  token: string
  password: string
}

class AuthError extends Error {
  code?: string
  field?: string
  constructor(message: string) {
    super(message)
    this.name = 'AuthError'
  }
}

export class AuthService {
  /**
   * Login user with credentials - Centralized Error Handling
   */
  async login(request: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>(AUTH_CONFIG.ROUTES.LOGIN, request)

      if (response.success && response.data) {
        // Store tokens and user data
        this.storeAuthData(response.data)

        return response.data
      }

      // api.post() never throws — bubble up the backend's message + details
      // (invalid credentials, MFA required, validation errors, etc.).
      throwBackendError(response, 'LOGIN_FAILED')
    } catch (error) {
      // Re-throw ApiException directly to preserve error structure for proper error message extraction
      if (error instanceof ApiException) {
        throw error
      }

      // For other errors, process and wrap
      const processedError = errorService.processError(error, { operation: 'login' })

      const authError = new AuthError(processedError.message)
      authError.code = processedError.code
      authError.field = processedError.field

      throw authError
    }
  }

  /**
   * Register new user
   */
  async register(request: RegisterRequest): Promise<RegisterResponse> {
    try {
      const response = await api.post<RegisterResponse>(AUTH_CONFIG.ROUTES.REGISTER, request)

      if (response.success && response.data) {
        return response.data
      }

      // api.post() never throws — bubble up the backend's message + field-level details
      // so the UI can surface validation errors (e.g. agreeToTerms, weak password).
      throwBackendError(response, 'REGISTER_FAILED')
    } catch (error) {
      // Re-throw ApiException directly to preserve error structure
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, { operation: 'register' })

      const authError = new AuthError(processedError.message)
      authError.code = processedError.code
      authError.field = processedError.field

      throw authError
    }
  }

  /**
   * Logout user and invalidate tokens
   */
  async logout(): Promise<void> {
    try {
      await api.post(AUTH_CONFIG.ROUTES.LOGOUT)
    } catch (error) {
      // Continue with logout even if API call fails
      logger.warn('Logout API call failed:', error)
    } finally {
      // Always clear local storage
      this.clearAuthData()
    }
  }

  /**
   * Logout from ALL devices/sessions for the current user.
   *
   * Calls backend POST /api/auth/logout-all which revokes every active session
   * for the user (audit-logged as LOGOUT_ALL). Always clears local auth data,
   * even when the network call fails — preserves the same resilient behavior
   * as `logout()` so the UI never gets stuck in a "logged in" state when the
   * user has explicitly asked to be logged out everywhere.
   *
   * Returns the number of sessions revoked by the server (defaults to 0 when
   * the response body is partial, e.g. response schema temporarily disabled).
   */
  async logoutAll(): Promise<{ sessionsRevoked: number }> {
    try {
      // Fastify rejects POST with `Content-Type: application/json` and empty body
      // (`FST_ERR_CTP_EMPTY_JSON_BODY` → 400). The endpoint expects no payload,
      // so we send an empty JSON object — backend ignores it.
      const response = await apiService.post<{ sessionsRevoked: number }>(
        '/api/auth/logout-all',
        {},
      )
      return response.data ?? { sessionsRevoked: 0 }
    } finally {
      // Always clear local auth data, even if the server response is partial
      // or the network call fails — user explicitly requested global logout.
      this.clearAuthData()
    }
  }

  /**
   * Refresh access token using refresh token
   *
   * NOTE: Two refresh code paths coexist in the frontend:
   *  - This method (legacy): used by `initializeAuth()` when the profile
   *    request fails with a non-401 error, and by callers that want the full
   *    `LoginResponse` (user + tokens) back.
   *  - `performAccessTokenRefreshAgainstBackend()` in `api-refresh.ts`: the
   *    canonical single-flight refresher used by the 401 auto-refresh
   *    interceptor in ApiService. Returns just the new access token.
   *
   * Both ultimately POST to `/api/auth/refresh`. If you change the refresh
   * contract, update BOTH places.
   */
  async refreshToken(): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>(AUTH_CONFIG.ROUTES.REFRESH)

    if (response.success && response.data) {
      // Store updated tokens and user data
      this.storeAuthData(response.data)

      return response.data
    }

    throwBackendError(response, 'TOKEN_REFRESH_FAILED')
  }

  /**
   * Reset password
   */
  async resetPassword(request: PasswordResetRequest): Promise<void> {
    try {
      const response = await api.post('/api/auth/forgot-password', request)

      if (!response.success) {
        throwBackendError(response, 'PASSWORD_RESET_FAILED')
      }
    } catch (error) {
      // Re-throw ApiException directly to preserve error structure for proper error message extraction
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, { operation: 'resetPassword' })

      const authError = new AuthError(processedError.message)
      authError.code = processedError.code
      authError.field = processedError.field

      throw authError
    }
  }

  /**
   * Set new password
   */
  async setNewPassword(request: PasswordResetConfirmRequest): Promise<void> {
    try {
      const response = await api.post('/api/auth/reset-password', request)

      if (!response.success) {
        throwBackendError(response, 'SET_NEW_PASSWORD_FAILED')
      }
    } catch (error) {
      // Re-throw ApiException directly to preserve error structure for proper error message extraction
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, { operation: 'setNewPassword' })

      const authError = new AuthError(processedError.message)
      authError.code = processedError.code
      authError.field = processedError.field

      throw authError
    }
  }

  /**
   * Verify email
   */
  async verifyEmail(token: string): Promise<void> {
    const response = await api.post('/api/auth/verify-email', { token })

    if (!response.success) {
      throwBackendError(response, 'EMAIL_VERIFICATION_FAILED')
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = storageService.getAuthToken()
    const user = storageService.getLocal(STORAGE_KEYS.USER_DATA)
    return Boolean(token && user)
  }

  /**
   * Get stored auth token
   */
  getAuthToken(): string | null {
    return storageService.getAuthToken()
  }

  /**
   * Get current user from storage
   */
  getCurrentUser(): UserResponse | null {
    const userData = storageService.getLocal(STORAGE_KEYS.USER_DATA)
    return userData as UserResponse | null
  }

  /**
   * Initialize authentication state on app startup
   */
  async initializeAuth(): Promise<{ user: UserResponse } | null> {
    try {
      // Check if we have stored credentials
      const token = this.getAuthToken()

      if (!token) {
        // No token stored, user is not authenticated
        this.clearAuthData()
        return null
      }

      // Set token in API service for subsequent requests
      apiService.setAuthToken(token)

      // Verify authentication with backend
      try {
        const response = await api.get<{ user: UserResponse }>(AUTH_CONFIG.ROUTES.PROFILE)
        if (response.success && response.data?.user) {
          // We're authenticated - store user data and return it
          const user = response.data.user // Extract user from nested data.user
          storageService.setLocal(STORAGE_KEYS.USER_DATA, user)
          return { user }
        }
        // ⚠️ `api.get` never throws: it RETURNS { success: false, error }. The
        // classification in the catch below (401 → clear, network/maintenance →
        // keep the session) therefore never ran, and EVERY failed check fell to
        // «Should not reach here» → clearAuthData(): a 5xx blip, a dropped
        // connection or a maintenance 503 on reload logged the user out
        // (e2e 2026-09-24). Hand the failure to that classification.
        if (!response.success) {
          throw new ApiException({
            status: response.error?.statusCode ?? 0,
            code: response.error?.code ?? 'NETWORK_ERROR',
            message: response.error?.message ?? '',
          })
        }
      } catch (error: unknown) {
        // If backend returns 401, the token/user is invalid - clear and return null
        const errObj = error as { status?: number; code?: string } | null
        // The account itself is closed (see account-refusal.ts): a refresh
        // would be refused the same way. Sign out and leave the reason for
        // the login page.
        const refusal = accountRefusalCodeOf(errObj)
        if (refusal) {
          rememberSignedOutReason(refusal)
          this.clearAuthData()
          return null
        }
        if (
          errObj?.status === 401 ||
          errObj?.code === 'USER_NOT_FOUND' ||
          errObj?.code === 'TOKEN_EXPIRED'
        ) {
          this.clearAuthData()
          return null
        }

        // Transport-level failures (request aborted by navigation, offline, DNS, timeout)
        // surface as `status: 0` with code NETWORK_ERROR / TIMEOUT in api.service. They
        // are NOT auth failures — the token may still be perfectly valid. Clearing auth
        // here would log the user out on every mid-navigation race. Instead, fall back
        // to the cached user from storage and let the next mount retry verification.
        // A maintenance refusal (503 MAINTENANCE_MODE) is not an auth failure
        // either: the refresh below would get the same 503 and clearAuthData()
        // logged every customer out of a perfectly valid session (e2e 2026-09-24).
        const isTransportFailure =
          errObj?.status === 0 ||
          errObj?.code === 'NETWORK_ERROR' ||
          errObj?.code === 'TIMEOUT' ||
          isMaintenanceRefusal(errObj)
        if (isTransportFailure) {
          const cachedUser = storageService.getLocal<UserResponse>(STORAGE_KEYS.USER_DATA)
          if (cachedUser) {
            return { user: cachedUser }
          }
          // No cached user — leave auth data intact (do not clear) and signal "unknown".
          // Returning null lets AuthProvider show LOGOUT, but the token stays in storage
          // so the next initialize attempt can succeed once the network settles.
          return null
        }

        // For other unexpected errors (e.g. backend 5xx), try token refresh as a recovery
        try {
          const refreshResponse = await this.refreshToken()
          return { user: refreshResponse.user }
        } catch (_refreshError) {
          this.clearAuthData()
          return null
        }
      }

      // Should not reach here, but clean up just in case
      this.clearAuthData()
      return null
    } catch (error) {
      logger.error('Failed to initialize auth:', error)
      this.clearAuthData()
      return null
    }
  }

  /**
   * Store authentication data
   */
  /**
   * Swap the stored access token for one scoped to another workspace (the
   * server also rotates the refresh cookie). Same storage as login.
   */
  adoptAccessToken(accessToken: string): void {
    storageService.setAuthToken(accessToken)
    apiService.setAuthToken(accessToken)
  }

  private storeAuthData(loginResponse: LoginResponse): void {
    // A new session begins: the notice about why the last one ended is done.
    clearSignedOutReason()
    const { user, tokens } = loginResponse

    const accessToken = tokens?.accessToken
    // refreshToken is stored as httpOnly cookie by backend

    if (!accessToken) {
      logger.error('STORE AUTH DATA - Access token not found, tokens object:', tokens)
      throw new ApiException({ status: 0, code: 'BAD_RESPONSE', message: '' })
    }

    // Store token WITHOUT client-side expiration
    // Server is the single source of truth for token validation
    // JWT token contains 'exp' claim - server will reject expired tokens with 401
    // HTTP client's auto-refresh mechanism will handle 401 responses transparently
    storageService.setAuthToken(accessToken)
    storageService.setLocal(STORAGE_KEYS.USER_DATA, user)

    // Set token in API service for subsequent requests
    apiService.setAuthToken(accessToken)
  }

  /**
   * Clear all authentication data
   *
   * Uses canonical `STORAGE_KEYS.*` constants to match the keys cleared by
   * `clearAuthDataLocally()` in `api-refresh.ts`. Both code paths MUST end up
   * clearing the SAME localStorage slots — otherwise stale data lingers across
   * logout boundaries.
   */
  private clearAuthData(): void {
    // Pre-existing sessions may leave orphan localStorage keys at `afcripto_REFRESH_TOKEN` and `afcripto_USER_DATA` from before this fix; re-login overwrites them and they are harmless.
    storageService.removeAuthToken()
    storageService.removeLocal(STORAGE_KEYS.REFRESH_TOKEN)
    storageService.removeLocal(STORAGE_KEYS.USER_DATA)
    apiService.removeAuthToken()
  }

  /**
   * MFA: Initialize MFA setup
   */
  async initializeMfaSetup(): Promise<MfaSetupData> {
    try {
      const response = await api.post<{ secret: string; qrCodeUrl: string; backupCodes: string[] }>(
        '/api/auth/mfa/setup',
      )

      if (response.success && response.data) {
        return response.data
      }

      // Backend rejected setup (e.g. MFA_ALREADY_ENABLED). Preserve its real
      // code + localized message instead of a synthetic BAD_RESPONSE.
      if (!response.success) {
        throwBackendError(response, 'MFA_SETUP_FAILED')
      }

      // success:true but payload missing — a genuinely malformed response
      throw new ApiException({
        status: 400,
        message: '',
        code: 'BAD_RESPONSE',
      })
    } catch (error) {
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, { operation: 'initializeMfaSetup' })
      throw new AuthError(processedError.message)
    }
  }

  /**
   * MFA: Verify TOTP code and enable MFA
   */
  async verifyMfa(code: string): Promise<MfaVerifyResponse> {
    try {
      const response = await api.post<MfaVerifyResponse>('/api/auth/mfa/verify', { code })

      if (response.success && response.data) {
        return response.data
      }

      // Backend rejected the code (INVALID_MFA_CODE, MFA_ALREADY_ENABLED, ...).
      // Preserve its real code + message so the UI shows "Código incorrecto",
      // NEVER collapse it into a synthetic BAD_RESPONSE ("Received an invalid
      // response from the server") which wrongly implies a server fault.
      if (!response.success) {
        throwBackendError(response, 'MFA_VERIFY_FAILED')
      }

      // success:true but payload missing — a genuinely malformed response
      throw new ApiException({
        status: 400,
        message: '',
        code: 'BAD_RESPONSE',
      })
    } catch (error) {
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, { operation: 'verifyMfa' })
      const authError = new AuthError(processedError.message)
      authError.code = processedError.code
      throw authError
    }
  }

  /**
   * MFA: Get MFA status
   */
  async getMfaStatus(): Promise<MfaStatusResponse> {
    try {
      const response = await api.get<MfaStatusResponse>('/api/auth/mfa/status')

      if (response.success && response.data) {
        return response.data
      }

      return { enabled: false, verifiedAt: null }
    } catch (error) {
      logger.warn('Failed to get MFA status:', error)
      return { enabled: false, verifiedAt: null }
    }
  }

  /**
   * MFA: Disable MFA
   */
  async disableMfa(password?: string, code?: string): Promise<void> {
    try {
      // The backend DELETE /auth/mfa/disable REQUIRES re-auth: a regular user
      // with MFA enabled must supply `password` OR `code`, else 403
      // MFA_DISABLE_REAUTH_REQUIRED. These travel in the request BODY (2nd arg
      // of api.delete), not as headers. Only include defined fields.
      const reauthBody: MfaDisableRequest = {
        ...(password !== undefined ? { password } : {}),
        ...(code !== undefined ? { code } : {}),
      }
      const response = await api.delete('/api/auth/mfa/disable', reauthBody)

      if (!response.success) {
        throwBackendError(response, 'MFA_DISABLE_FAILED')
      }
    } catch (error) {
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, { operation: 'disableMfa' })
      const authError = new AuthError(processedError.message)
      authError.code = processedError.code
      throw authError
    }
  }

  /**
   * MFA: Regenerate backup codes
   */
  async regenerateBackupCodes(): Promise<string[]> {
    try {
      const response = await api.post<{ backupCodes: string[] }>(
        '/api/auth/mfa/backup-codes/regenerate',
      )

      if (response.success && response.data) {
        return response.data.backupCodes
      }

      // Backend rejected regenerate (e.g. MFA_NOT_ENABLED). Preserve its real
      // code + localized message instead of a synthetic BAD_RESPONSE.
      if (!response.success) {
        throwBackendError(response, 'MFA_BACKUP_CODES_FAILED')
      }

      // success:true but payload missing — a genuinely malformed response
      throw new ApiException({
        status: 400,
        message: '',
        code: 'BAD_RESPONSE',
      })
    } catch (error) {
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, {
        operation: 'regenerateBackupCodes',
      })
      throw new AuthError(processedError.message)
    }
  }

  /**
   * MFA: Complete login with MFA challenge
   */
  async mfaChallenge(
    email: string,
    code: string,
    useBackupCode = false,
    preAuthToken?: string,
  ): Promise<LoginResponse> {
    try {
      // The backend REQUIRES the short-lived pre-auth token (minted by /auth/login
      // when MFA is required) before it will issue a session — proof the password
      // step was completed. It is read from the body `preAuthToken` OR the httpOnly
      // `mfaPreAuth` cookie. The generic api client does NOT send cookies
      // cross-origin, so we thread the token through the body to stay origin-safe.
      const response = await api.post<LoginResponse>('/api/auth/mfa/challenge', {
        email,
        code,
        useBackupCode,
        ...(preAuthToken !== undefined ? { preAuthToken } : {}),
      })

      if (response.success && response.data) {
        // Store tokens and user data
        this.storeAuthData(response.data)
        return response.data
      }

      // Backend rejected the challenge (invalid/expired code, expired pre-auth
      // token...). Preserve its code + localized message for the UI — a
      // synthetic BAD_RESPONSE is only for malformed success responses.
      if (!response.success) {
        throwBackendError(response, 'MFA_CHALLENGE_FAILED')
      }

      // success:true but payload missing — a genuinely malformed response
      throw new ApiException({
        status: 400,
        message: '',
        code: 'BAD_RESPONSE',
      })
    } catch (error) {
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, { operation: 'mfaChallenge' })
      const authError = new AuthError(processedError.message)
      authError.code = processedError.code
      throw authError
    }
  }

  /**
   * Login with MFA support (checks if MFA is required)
   */
  async loginWithMfa(
    request: LoginRequest,
  ): Promise<LoginResponse | { mfaRequired: true; email: string; preAuthToken?: string }> {
    try {
      const response = await api.post<
        LoginResponse | { mfaRequired: true; email: string; preAuthToken?: string }
      >(AUTH_CONFIG.ROUTES.LOGIN, request)

      if (response.success && response.data) {
        // Check if MFA is required
        if ('mfaRequired' in response.data && response.data.mfaRequired) {
          return response.data
        }

        // Normal login response - store tokens
        this.storeAuthData(response.data as LoginResponse)
        return response.data as LoginResponse
      }

      // Backend rejected the login (invalid credentials, 2FA required, rate
      // limit...). Preserve its code + localized message + field details —
      // NEVER collapse a real backend error into a synthetic BAD_RESPONSE,
      // or the UI loses the specific message and shows a generic one.
      if (!response.success) {
        throwBackendError(response, 'LOGIN_FAILED')
      }

      // success:true but payload missing — a genuinely malformed response
      throw new ApiException({
        status: 400,
        message: '',
        code: 'BAD_RESPONSE',
      })
    } catch (error) {
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, { operation: 'login' })
      const authError = new AuthError(processedError.message)
      authError.code = processedError.code
      throw authError
    }
  }

  /**
   * Account Deletion: Request account deletion
   */
  async requestAccountDeletion(request: { reason?: string }): Promise<{ message: string }> {
    try {
      const response = await api.post<{ message: string }>(
        '/api/auth/account-deletion/request',
        request,
      )

      if (response.success && response.message) {
        return { message: response.message }
      }

      throw new ApiException({
        status: 400,
        message: '',
        code: 'BAD_RESPONSE',
      })
    } catch (error) {
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, {
        operation: 'requestAccountDeletion',
      })
      const authError = new AuthError(processedError.message)
      authError.code = processedError.code
      throw authError
    }
  }

  /**
   * Account Deletion: Confirm account deletion with token
   */
  async confirmAccountDeletion(request: {
    token: string
  }): Promise<{ message: string; scheduledFor?: string }> {
    try {
      const response = await api.post<{ message: string; scheduledFor?: string }>(
        '/api/auth/account-deletion/confirm',
        request,
      )

      if (response.success) {
        // Backend is authoritative for success copy; if omitted, let the UI
        // layer supply its own i18n string rather than hardcoding one here.
        return {
          message: response.message ?? '',
          scheduledFor: response.data?.scheduledFor,
        }
      }

      throw new ApiException({
        status: 400,
        message: '',
        code: 'BAD_RESPONSE',
      })
    } catch (error) {
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, {
        operation: 'confirmAccountDeletion',
      })
      const authError = new AuthError(processedError.message)
      authError.code = processedError.code
      throw authError
    }
  }

  /**
   * Account Deletion: Cancel account deletion
   */
  async cancelAccountDeletion(): Promise<{ message: string }> {
    try {
      const response = await api.post<{ message: string }>('/api/auth/account-deletion/cancel')

      if (response.success && response.message) {
        return { message: response.message }
      }

      throw new ApiException({
        status: 400,
        message: '',
        code: 'BAD_RESPONSE',
      })
    } catch (error) {
      if (error instanceof ApiException) {
        throw error
      }

      const processedError = errorService.processError(error, {
        operation: 'cancelAccountDeletion',
      })
      const authError = new AuthError(processedError.message)
      authError.code = processedError.code
      throw authError
    }
  }

  /**
   * Account Deletion: Get account deletion status
   */
  async getAccountDeletionStatus(): Promise<{
    deletionRequest: {
      requestId: string
      status: 'pending' | 'scheduled' | 'cancelled' | 'completed'
      scheduledFor: string
      gracePeriodEnds: string
      canCancelUntil: string
    } | null
  }> {
    try {
      const response = await api.get<{
        deletionRequest: {
          requestId: string
          status: 'pending' | 'scheduled' | 'cancelled' | 'completed'
          scheduledFor: string
          gracePeriodEnds: string
          canCancelUntil: string
        } | null
      }>('/api/auth/account-deletion/status')

      if (response.success && response.data) {
        return response.data
      }

      return { deletionRequest: null }
    } catch (error) {
      logger.warn('Failed to get account deletion status:', error)
      return { deletionRequest: null }
    }
  }
}

export const authService = new AuthService()
